import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IconSearch, IconAlertCircle, IconUsers, IconChevronRight, IconStar, IconCalendar,
} from '@tabler/icons-react';
import AdminLayout from '../../components/layout/AdminLayout';

interface ApiCustomer {
  id: string;
  name: string;
  phone: string;
  email: string | null;
  memberNumber: string;
  dob: string | null;
  loyaltyPoints: number;
  totalSpentCents: number;
  lastVisitAt: string | null;
  createdAt: string;
}

function fmtR(cents: number) {
  return `R ${(cents / 100).toFixed(2)}`;
}

function fmtRelative(iso: string | null) {
  if (!iso) return '—';
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const day = 24 * 60 * 60 * 1000;
  if (diff < day) return 'today';
  if (diff < 2 * day) return 'yesterday';
  if (diff < 7 * day) return `${Math.floor(diff / day)} days ago`;
  if (diff < 30 * day) return `${Math.floor(diff / (7 * day))} weeks ago`;
  return d.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' });
}

export default function AdminCustomersPage() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState<ApiCustomer[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    try {
      const r = await fetch(`/api/customers/search?q=${encodeURIComponent(search)}`, { credentials: 'include' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setCustomers(data.customers ?? []);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => { void load(); }, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search]);

  const filtered = useMemo(() => customers, [customers]);

  return (
    <AdminLayout
      title="Customers"
      subtitle="Directory · loyalty totals · house accounts"
    >
      {error && (
        <div className="rounded-md bg-danger-soft border border-danger/30 p-3 flex items-start gap-2 mb-5">
          <IconAlertCircle size={18} className="text-danger flex-shrink-0 mt-0.5" />
          <p className="text-13 font-semibold text-danger">{error}</p>
        </div>
      )}

      <section className="rounded-lg bg-surface border border-border p-4 mb-4">
        <div className="relative">
          <IconSearch size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, phone, or member number"
            className="w-full h-10 pl-9 pr-3 rounded-md bg-light border border-border focus:border-primary outline-none text-13 font-semibold text-heading"
          />
        </div>
      </section>

      <section className="rounded-lg bg-surface border border-border p-4">
        {loading ? (
          <p className="text-13 italic text-gray-700">Loading…</p>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 rounded-2xl bg-light text-gray-700 inline-flex items-center justify-center mb-3">
              <IconUsers size={22} stroke={2} />
            </div>
            <p className="text-14 font-bold text-heading">No customers</p>
            <p className="text-13 font-medium text-gray-700 mt-1">
              {search.trim() ? 'Try a different search.' : 'Add customers from the POS using "+ New customer" on the attach popover, or the loyalty onboarding page.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-13">
              <thead>
                <tr className="text-left text-12 font-bold uppercase tracking-wider text-gray-700">
                  <th className="py-2 pr-3">Name</th>
                  <th className="py-2 pr-3">Member #</th>
                  <th className="py-2 pr-3">Phone</th>
                  <th className="py-2 pr-3 text-right">Points</th>
                  <th className="py-2 pr-3 text-right">Spent</th>
                  <th className="py-2 pr-3">Last visit</th>
                  <th className="py-2 pr-3"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr
                    key={c.id}
                    onClick={() => navigate(`/admin/customers/${c.id}`)}
                    className="border-t border-border-soft cursor-pointer hover:bg-light"
                  >
                    <td className="py-2.5 pr-3">
                      <p className="font-bold text-heading">{c.name}</p>
                      {c.email && <p className="text-12 text-gray-700">{c.email}</p>}
                    </td>
                    <td className="py-2.5 pr-3 text-gray-700 font-mono text-12">{c.memberNumber}</td>
                    <td className="py-2.5 pr-3 text-gray-700 tabular-nums">{c.phone}</td>
                    <td className="py-2.5 pr-3 text-right">
                      <span className="inline-flex items-center gap-1 text-warning font-bold tabular-nums">
                        <IconStar size={12} stroke={2} fill="currentColor" />
                        {c.loyaltyPoints}
                      </span>
                    </td>
                    <td className="py-2.5 pr-3 text-right font-bold text-heading tabular-nums">{fmtR(c.totalSpentCents)}</td>
                    <td className="py-2.5 pr-3 text-gray-700 text-12">{fmtRelative(c.lastVisitAt)}</td>
                    <td className="py-2.5 pr-3 text-right">
                      <IconChevronRight size={16} stroke={2.25} className="text-gray-700" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <p className="text-12 font-medium text-gray-700 mt-4 inline-flex items-center gap-1.5">
        <IconCalendar size={12} stroke={2.25} />
        Customers earn 1 point per R1 net spend (tip excluded). Tier engine ships in Phase 8.
      </p>
    </AdminLayout>
  );
}
